import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, withText } from 'preact-i18n';

import localUtil from '../../lib/util';

import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import { animalAge } from '../../lib/animalAge';

// util.age returns a raw object (not a string) for missing/future/unparseable
// birth dates - rendering that bare in JSX crashes Preact. Coerce to string.
function ageText(birth) {
    if (!birth) {
        return '';
    }
    const age = animalAge(birth);
    return typeof age === 'string' ? age : '';
}

// Animal-first search for the calendar booking drawer (TICKET-01294): search
// the animal directly (like the journal does) instead of owner-first. Picking
// an animal fills newCalendarEvent.animals + title exactly like the pill
// click in searchVisitor.js, and shows the owner + their other animals.
@withText(() => ({
    animalTitle: <Text id='dyrejournal.animal.search-title'>Search for animal</Text>,
}))
@observer
class SearchAnimal extends Component {
	constructor(props) {
        super(props);
        this.state = {
            owner: null,
            ownerAnimals: [],
        };
    }

    setAnimalOnEvent = animal => {
        const { calendarEventStore } = this.props.stores;
        calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'animals', [{
            id: animal.id,
            name: animal.name,
            breed: animal.breed,
            species: animal.species,
            birth: animal.birth,
            chipId: animal.chipId,
        }]);
        calendarEventStore.updateObjectKeyValue('newCalendarEvent', 'title', `${animal.name} (${animal.species})`);
    }

    searchAnimalCallback = async animal => {
        if (!animal || !animal.id) {
            return;
        }
        const { animalStore, visitorStore } = this.props.stores;
        this.setAnimalOnEvent(animal);

        if (!animal.owner) {
            this.setState({ owner: null, ownerAnimals: [] });
            return;
        }
        // The search runs with addData ['owners'], so the owner is usually
        // already in animalStore.owners - fall back to a direct load if not.
        let owner = (animalStore.owners || []).find(o => o.id === animal.owner);
        if (!owner) {
            const response = await visitorStore.load({
                query: {
                    id: [animal.owner],
                },
                skipUpdate: true,
            });
            owner = response && response.length > 0 ? response[0] : null;
        }
        const ownerAnimals = await animalStore.load({
            query: {
                owner: [animal.owner],
            },
            skipUpdate: true,
        });
        this.setState({ owner: owner || null, ownerAnimals: ownerAnimals || [] });
    }

    onClickChooseAnimal = e => {
        const { id } = e.target.closest('.resultLine').dataset;
        const { ownerAnimals } = this.state;
        const idInt = parseInt(id, 10);
        const animal = ownerAnimals.find(a => a.id === idInt);
        if (animal) {
            this.setAnimalOnEvent(animal);
        }
    }

    render() {
        const { animalTitle } = this.props;
        const { animalStore } = this.props.stores;
        const { owner, ownerAnimals } = this.state;
        const { owners = [] } = animalStore;

        return(<>
            <div class=''>
                <DyrejournalSaleSearch
                    stores={this.props.stores}
                    key={'calendar-animal'}
                    title={animalTitle}
                    holdingStore={animalStore}
                    searchResultList={'animalResults'}
                    addData={['owners']}
                    skipSaleUpdate={true}
                    skipDyreIdImport={true}
                    searchResultsLine={(e, idx, selectedIndex) => {
                        const lineOwner = owners.find(o => o.id === e.owner);
                        return(<>
                            <div class={`d-flex flex-row justify-content-between px-3 py-1 ${idx === selectedIndex ? 'selected' : ''} ${e.isLost ? 'bg-danger text-white rounded-lg' : ''}`}>
                                <div class='ms-2 font-style-narrow'>
                                    {e.isLost ? <i class='fa-duotone fa-magnifying-glass-location me-1' /> : ''}
                                    {e.isDececed ? <i class='fa-duotone fa-tombstone' /> : ''} {e.name} ({e.breed})
                                    <nobr>{e.chipId ? <i class='fa-solid fa-microchip text-muted me-2' /> : ''} {ageText(e.birth)}</nobr>
                                </div>
                                {lineOwner ? <>
                                    <div class='ms-2 font-weight-lighter font-style-narrow'>
                                        <nobr>
                                            <i class='fa-regular fa-user text-muted me-2' /> {lineOwner?.firstname} {lineOwner?.lastname}
                                        </nobr>
                                    </div>
                                </> : <></>}
                            </div>
                        </>);
                    }}
                    callback={this.searchAnimalCallback}
                    ref={c => this.animalSearchRef = c}
                />
            </div>
            <div>
                {owner ? <>
                    <div class='mt-2'>
                        <small class='font-weight-lighter'>
                            <i class='fa-regular fa-user text-muted me-1' />
                            <Text id='calendarEvent.animal-owner'>Owner</Text>: {localUtil.showName(owner)} {owner.cellphone}
                        </small>
                    </div>
                </> : null}
                {(ownerAnimals && ownerAnimals.length > 0) && <>
                    <div class='mt-3'>
                        <div class='font-weight-lighter text-muted'>
                            <small class='text-uppercase'>
                                <Text id='dyrejournal.animal.owned-by-visitor'>Animals owned by visitor</Text>
                            </small>
                        </div>
                        <div class='d-flex flex-wrap overflow-auto' style='max-height: 200px;'>
                            {ownerAnimals.filter(e => !e.isDececed && !e.isRelocated && !e.isInactive).map((e, idx) => (
                                <small
                                    key={e.id}
                                    class={`${e.isLost ? 'bg-danger text-white' : e.isDececed ? 'bg-warning' : e.isRelocated ? 'bg-info' : e.isInactive ? 'bg-secondary text-white' : 'bg-light'} resultLine d-flex justify-content-between me-2 mt-1 px-2 font-weight-normal rounded-lg`}
                                    style='cursor: pointer; width: 32%;'
                                    onClick={this.onClickChooseAnimal}
                                    data-idx={idx}
                                    data-id={e.id}
                                    data-name={e.name}
                                >
                                    {e.isLost && <i class='fa-duotone fa-magnifying-glass-location me-1' />}
                                    {e.isDececed && <i class='fa-duotone fa-tombstone me-1' />}
                                    {e.isRelocated && <i class='fa-duotone fa-arrow-up-right-from-square me-1' />}
                                    {e.isInactive && <i class='fa-duotone fa-ban me-1' />}
                                    <div class='flex-fill'>{e.name} ({e.species})</div>
                                    <div class='ms-2 text-end'><nobr>{e.chipId ? <i class='fa-solid fa-microchip text-muted me-2' /> : ''} {ageText(e.birth)}</nobr></div>
                                </small>
                            ))}
                        </div>
                    </div>
                </>}
            </div>
        </>);
    }
}

export default SearchAnimal;
